export function parseODataError(text: string): string | null {
  try {
    const json = JSON.parse(text);
    return json?.error?.message ?? null;
  } catch {
    return null;
  }
}

/**
 * BC's OData error envelope can carry a `code` alongside `message` (e.g. an AL ErrorInfo's
 * error code). Captured separately from the human-readable message so a caller can key
 * off it programmatically -- e.g. to tell an auth/claims-flavored failure apart from an
 * unrelated server error -- without parsing it back out of formatted text. Not guaranteed
 * to be populated; BC's generic "internal error" wrapper for an unhandled exception may
 * carry no code at all.
 */
export function parseODataErrorCode(text: string): string | null {
  try {
    const json = JSON.parse(text);
    return json?.error?.code ?? null;
  } catch {
    return null;
  }
}

/**
 * True when a BC OData error message is BC's "The server has rejected the client
 * credentials." response. In practice this is what BC returns for an AAD identity that
 * has no BC user record in that particular environment -- the same underlying condition
 * as an empty userPermissions/user_details lookup, just surfacing one level earlier
 * (on the base companies list call, before any per-user check runs). Callers treat it as
 * "no access at this endpoint", not as a real auth failure worth alarming the user about.
 */
export function isCredentialsRejectedError(message: string): boolean {
  return /rejected the client credentials/i.test(message);
}

/**
 * Formats a failed BC response into an Error message, keeping the HTTP status alongside
 * BC's own parsed message rather than only falling back to it when no message was found.
 * BC's message is frequently a generic wrapper around an unhandled server-side exception
 * (e.g. "Er is een interne fout opgetreden") -- it deliberately never exposes the real
 * cause to external API callers -- so the status code is the only extra signal a bug
 * report carries for telling a genuine server error (500) apart from a validation (400)
 * or auth (401/403) failure without needing to reproduce it.
 */
export function formatODataHttpError(status: number, responseText: string): string {
  const odataMsg = parseODataError(responseText);
  if (odataMsg) return `${odataMsg} (HTTP ${status})`;

  // No OData error envelope at all -- BC's own OData layer always sends one, so a bare status
  // like this usually means something in front of BC (a proxy/WAF/security appliance, IIS)
  // answered instead, and its body (often an HTML "request rejected ... support ID: ..."
  // page) is the only clue to which one. Previously discarded, leaving just "HTTP 403".
  const snippet = summarizeResponseBody(responseText);
  return snippet ? `HTTP ${status}: ${snippet}` : `HTTP ${status}`;
}

/** Tags stripped, whitespace collapsed, capped -- short enough for a status banner, and only
 *  ever rendered as text (textContent), never as HTML. */
export function summarizeResponseBody(text: string, maxLength = 160): string {
  const plain = String(text ?? "")
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > maxLength ? `${plain.slice(0, maxLength)}...` : plain;
}

export interface BcHttpErrorDetails {
  code?: string | null;
  // Set only by callers that generate and send their own x-ms-client-request-id (see
  // linkOrderDocument) -- lets a specific failed attempt be pointed out precisely when
  // asking BC/AL support to search their own telemetry, independent of whatever (if
  // anything) BC itself echoes back.
  clientRequestId?: string | null;
  // BC's own request/correlation id, read back from the response headers when present
  // (header name isn't guaranteed -- see readBcResponseRequestId). Null if BC didn't
  // return one we recognize.
  bcRequestId?: string | null;
}

/**
 * Thrown for a failed BC HTTP response, carrying the numeric status alongside the
 * formatted message (see formatODataHttpError) so a caller can tell a genuine
 * server-side failure (5xx -- possibly transient, e.g. a record lock that clears on its
 * own) apart from a client-side one (4xx -- validation/auth, which will just fail the
 * same way again) without re-parsing the status back out of the message text. Still a
 * plain Error to every caller that doesn't care (instanceof Error is unaffected).
 */
export class BcHttpError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly clientRequestId: string | null;
  readonly bcRequestId: string | null;
  constructor(status: number, message: string, details: BcHttpErrorDetails = {}) {
    super(message);
    this.name = "BcHttpError";
    this.status = status;
    this.code = details.code ?? null;
    this.clientRequestId = details.clientRequestId ?? null;
    this.bcRequestId = details.bcRequestId ?? null;
  }
}

/**
 * Escape a value for use inside an OData single-quoted string literal.
 *
 * Only single quotes need escaping (' -> '') per the OData ABNF grammar.
 * Characters like %, *, and filter operators are not special inside string
 * literals -- they are only meaningful at the filter-expression level, which
 * is always constructed from trusted templates in this codebase.
 */
export function escapeODataLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

/**
 * Assemble a `?a=...&b=...` query string from OData system query options, percent-encoding
 * every value with encodeURIComponent.
 *
 * escapeODataLiteral only makes a value safe *inside* an OData string literal (' -> '');
 * it does nothing about characters that are structurally significant in a URL. A $filter
 * value here routinely carries user data -- an attachment file name, a typed search term,
 * an email address -- and a literal `&`, `#`, `+`, `?` or space in that data would
 * otherwise truncate or corrupt the query once it is concatenated into the request URL
 * (e.g. a file named "Invoice & Receipt.pdf" cut the $filter off at the `&`, so the
 * "already uploaded" check silently failed for that file).
 *
 * Keys are emitted in the given order and left un-encoded (they are always trusted
 * constants like `$filter`). An entry whose value is undefined is skipped, so an optional
 * option such as $orderby can be passed straight through.
 */
export function buildODataQuery(params: Record<string, string | undefined>): string {
  const pairs = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[1] !== undefined,
  );
  if (pairs.length === 0) return "";
  return "?" + pairs.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
}
